import { getKPICurrency } from '@modules/order/utils/get-kpi-currency'
import { useCallback, useMemo } from 'react'
import {
  checkWhetherBookedOrNot,
  useEnhancedProductStatus
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import { getBookedCells } from '../get-booked-cells'
import { useTranslation } from 'react-i18next'
import filter from 'lodash/filter'
import isNumber from 'lodash/isNumber'
import { PRODUCT_OPERATIONS_LOADERS } from '@modules/order/stores/view/List/ListDataStore'
import {
  getUnExcludedDoorIds,
  getUnExcludedDoors
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import { isCaptureSizeEnabled } from '@modules/order/utils/table-column-utils'
import EnhancedProductQuantitiesUIStore from '@modules/showroom/productDetail/EnhancedProductQuantities/stores/EnhancedProductQuantitiesUIStore'

interface IEnhancedBookQuantityProps {
  rowData: any
  orderType: string
  summary: any
  getBookedDoors: Function
  isValueSelected: boolean
  isRetail: boolean
  itemIndexObjectStore: any
  setIsQuantitiesUpdating?: Function
  setUpdatedQuantities?: Function
  bookUnbookDoors?: EnhancedProductQuantitiesUIStore['bookUnbookDoors']
}

export const useEnhancedBookQuantity = ({
  rowData,
  orderType,
  summary,
  getBookedDoors,
  isValueSelected,
  isRetail,
  itemIndexObjectStore,
  setIsQuantitiesUpdating,
  setUpdatedQuantities,
  bookUnbookDoors
}: IEnhancedBookQuantityProps) => {
  const { t } = useTranslation()
  const rowDataId = rowData?.id
  const currency = getKPICurrency(orderType, summary)
  const captureSizeEnabled = isCaptureSizeEnabled(summary)
  const sizes = rowData?.['original-sizes-id'] || []
  const bookedDoors = getBookedDoors(rowDataId, summary?.doorOrders)
  const excludedInDoors = rowData?.excludedInDoors
  const areAllDoorsBooked = useMemo(() => {
    // check for order-type door (1 object of doorOrder) else client order (array of object of door orders)
    return checkWhetherBookedOrNot(
      excludedInDoors,
      filter(summary?.doorOrders, doorOrder => !doorOrder?.deleted),
      bookedDoors,
      true
    )
  }, [summary?.doorOrders, bookedDoors, excludedInDoors])

  const bookedCells = getBookedCells(rowData, isRetail, currency, isValueSelected, t)
  const clientOrderId = summary?.clientOrder?.clientOrderId
  const index = rowDataId ? itemIndexObjectStore.getIndexByKey(rowDataId) : null
  const isBookingAllInProgress = isNumber(index)
    ? itemIndexObjectStore.isObjectDataLoading(
        index,
        PRODUCT_OPERATIONS_LOADERS.BOOKING_ALL_DOORS
      )
    : false
  const isUnBookingAllInProgress = isNumber(index)
    ? itemIndexObjectStore.isObjectDataLoading(
        index,
        PRODUCT_OPERATIONS_LOADERS.UNBOOKING_ALL_DOORS
      )
    : false
  const isAnyBookUnbookInProgress = isNumber(index)
    ? itemIndexObjectStore.isObjectDataLoading(
        index,
        PRODUCT_OPERATIONS_LOADERS.BOOK_UNBOOK_IN_PROGRESS
      )
    : false

  let areAllDoorsBookedOptimistic: boolean
  if (isAnyBookUnbookInProgress) {
    areAllDoorsBookedOptimistic = isBookingAllInProgress && !isUnBookingAllInProgress
  } else {
    areAllDoorsBookedOptimistic = areAllDoorsBooked
  }
  const isProductActive: boolean = useEnhancedProductStatus(rowData)

  const getDoorOrderIds = useCallback(
    isBook => {
      return isBook
        ? filter(
            filter(summary.doorOrders, doorOrder => !doorOrder?.deleted),
            doorOrder => !bookedDoors.includes(doorOrder?.door?.id)
          )
        : filter(summary.doorOrders, doorOrder => !doorOrder?.deleted)
    },
    [summary, bookedDoors]
  )

  const statusChangeHandler = useCallback(
    (e, checked) => {
      setIsQuantitiesUpdating?.(true)
      bookUnbookDoors(
        clientOrderId,
        rowDataId,
        getUnExcludedDoorIds(excludedInDoors, getDoorOrderIds(checked), checked),
        checked,
        getUnExcludedDoors(excludedInDoors, getDoorOrderIds(checked), checked),
        true // isAll
      ).then(() => {
        setUpdatedQuantities?.()
        setIsQuantitiesUpdating?.(false)
      })
    },
    [
      rowDataId,
      excludedInDoors,
      clientOrderId,
      bookUnbookDoors,
      getDoorOrderIds,
      setUpdatedQuantities,
      setIsQuantitiesUpdating
    ]
  )

  return {
    currency,
    rowDataId,
    isProductActive,
    isAnyBookUnbookInProgress,
    isBookingAllInProgress,
    isUnBookingAllInProgress,
    bookedCells,
    areAllDoorsBooked: areAllDoorsBookedOptimistic,
    captureSizeEnabled,
    sizes,
    getDoorOrderIds,
    statusChangeHandler
  }
}
